import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';

type FormState = {
  displayName: string;
  mainSocialLink: string;
  email: string;
  capcutUid: string;
  ccid: string;
  isUS: 'yes' | 'no' | '';
  country: string;
  discordUsername: string;
  discordLanguage: 'English' | 'Español' | 'Português';
  otherHandles: string;
  postLink: string;
  posterName: string;
};

type SubmitSuccess = {
  ok: true;
  registrationNumber: string;
  recordId: string;
};

type SubmitFailure = {
  ok: false;
  message: string;
  details?: string;
};

const DISCORD_URL = 'https://discord.gg/EfQVu7NfDq';
const CONTACT_EMAIL = 'jinwei711@bytedance.com';

const introSlides = [
  '/intro/slide-1.png',
  '/intro/slide-2.png',
  '/intro/slide-3.png',
  '/intro/slide-4.png',
  '/intro/slide-5.png',
];

const creators = [
  {
    name: 'Jordan Daniel Chesney',
    role: 'AI video creator',
    avatar: '/creators/jordan.jpg',
    link: 'https://x.com/jordandchesney',
  },
  {
    name: 'Josh Daws',
    role: 'Creative technologist',
    avatar: '/creators/josh.jpg',
    link: 'https://x.com/JoshDaws',
  },
  {
    name: 'PJ Ace',
    role: 'AI storytelling creator',
    avatar: '/creators/pj.jpg',
    link: 'https://x.com/PJaccetturo',
  },
];

const benefits = [
  ['AI Credits & Pro Access', 'Create without limits using advanced AI tools.'],
  ['Early Access', 'Try new CapCut features and ByteDance AI models first.'],
  ['Distribution & Exposure', 'Get your work seen across CapCut channels and beyond.'],
  ['Cash Rewards', 'Earn from high-performing work and campaign opportunities.'],
  ['Creator Community', 'Meet other top AI creators and grow together.'],
];

const incentives = [
  ['Start Strong', '10,000 credits / mo + 1 Pro / mo'],
  ['Create & Share', '1,000 credits / piece, up to 20 / mo'],
  ['Go Viral', '$1,000 / piece, up to 5 / mo'],
  ['Build Bigger', 'Up to $100,000 / project'],
  ['Get Featured', '10,000 credits / piece + extra boosting'],
];

const initialForm: FormState = {
  displayName: '',
  mainSocialLink: '',
  email: '',
  capcutUid: '',
  ccid: '',
  isUS: '',
  country: '',
  discordUsername: '',
  discordLanguage: 'English',
  otherHandles: '',
  postLink: '',
  posterName: '',
};

function classNames(...items: Array<string | false | undefined>) {
  return items.filter(Boolean).join(' ');
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isValidUrl(value: string) {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

function validateByStep(step: number, form: FormState): string | null {
  if (step === 1) {
    if (!form.displayName.trim()) return 'Display Name is required.';
    if (!isValidUrl(form.mainSocialLink)) return 'Please enter a valid primary social media link.';
    if (!isValidEmail(form.email)) return 'Please enter a valid email address.';
    if (!/^\d{19}$/.test(form.capcutUid.trim())) return 'CapCut UID must be exactly 19 digits.';
    if (!/^\d{8,10}$/.test(form.ccid.trim())) return 'CCID must be 8–10 digits. It is not your username handle.';
    if (!form.isUS) return 'Please select whether you are in the US region.';
    if (!form.country.trim()) return 'Please enter your registration country.';
  }
  if (step === 3) {
    if (!form.posterName.trim()) return 'Poster display name is required before submission.';
  }
  return null;
}

function validateAll(form: FormState): string | null {
  for (const step of [1, 2, 3]) {
    const error = validateByStep(step, form);
    if (error) return error;
  }
  return null;
}

function IntroCarousel() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setIndex((value) => (value + 1) % introSlides.length), 4200);
    return () => window.clearInterval(timer);
  }, []);

  const move = (delta: number) => setIndex((value) => (value + delta + introSlides.length) % introSlides.length);

  return (
    <div className="glass relative overflow-hidden rounded-[2rem] p-2">
      <div className="relative aspect-[1696/927] overflow-hidden rounded-[1.55rem] bg-black">
        {introSlides.map((slide, slideIndex) => (
          <img
            key={slide}
            className={classNames(
              'absolute inset-0 h-full w-full object-cover transition-opacity duration-700',
              slideIndex === index ? 'opacity-100' : 'opacity-0',
            )}
            src={slide}
            alt={`CapCut CPP Intro Deck slide ${slideIndex + 1}`}
          />
        ))}
        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/70 to-transparent p-4 sm:p-6">
          <button className="rounded-full border border-white/20 bg-black/35 px-4 py-2 text-sm font-bold backdrop-blur" onClick={() => move(-1)} type="button">←</button>
          <div className="flex gap-2">
            {introSlides.map((_, dotIndex) => (
              <button
                key={dotIndex}
                className={classNames('h-2 rounded-full transition-all', dotIndex === index ? 'w-8 bg-cyan-400' : 'w-2 bg-white/40')}
                onClick={() => setIndex(dotIndex)}
                aria-label={`Go to intro slide ${dotIndex + 1}`}
                type="button"
              />
            ))}
          </div>
          <button className="rounded-full border border-white/20 bg-black/35 px-4 py-2 text-sm font-bold backdrop-blur" onClick={() => move(1)} type="button">→</button>
        </div>
      </div>
    </div>
  );
}

function PosterCanvas({ name }: { name: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const safeName = name.trim() || 'Your Creator Name';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = 1620;
    const height = 2160;
    canvas.width = width;
    canvas.height = height;

    const drawName = () => {
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0, 245, 212, 0.38)';
      ctx.shadowBlur = 18;
      ctx.fillStyle = '#ffffff';
      const fontSize = safeName.length > 18 ? 58 : 70;
      ctx.font = `italic 800 ${fontSize}px Inter, Arial, sans-serif`;
      ctx.letterSpacing = '-0.02em';
      wrapTextCentered(ctx, safeName, width / 2, 760, 820, fontSize * 1.06);
      ctx.restore();
    };

    const drawFallback = () => {
      const bg = ctx.createLinearGradient(0, 0, width, height);
      bg.addColorStop(0, '#050607');
      bg.addColorStop(0.5, '#071619');
      bg.addColorStop(1, '#001a22');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = 'rgba(0,245,212,0.18)';
      ctx.fillRect(120, 120, width - 240, height - 240);
      drawName();
    };

    const template = new Image();
    template.onload = () => {
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(template, 0, 0, width, height);
      drawName();
    };
    template.onerror = drawFallback;
    template.src = '/poster/cpp-invite-template.png';
  }, [safeName]);

  const downloadPoster = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `capcut-cpp-welcome-${safeName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)] lg:items-center">
      <canvas ref={canvasRef} className="poster-canvas mx-auto max-w-[360px]" aria-label="CPP welcome poster preview" />
      <div className="space-y-4">
        <p className="text-sm uppercase tracking-[0.28em] text-cyan-300">Live poster preview</p>
        <h3 className="text-3xl font-black tracking-[-0.05em] text-white sm:text-5xl">Download your welcome poster.</h3>
        <p className="text-white/70">Use it for your Instagram / TikTok / YouTube / X announcement after your CPP application is complete.</p>
        <button className="capcut-gradient rounded-2xl px-6 py-4 font-black text-black shadow-[0_18px_50px_rgba(0,245,212,0.24)]" onClick={downloadPoster} type="button">Download Poster</button>
      </div>
    </div>
  );
}

function wrapTextCentered(ctx: CanvasRenderingContext2D, text: string, centerX: number, centerY: number, maxWidth: number, lineHeight: number) {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const testLine = line ? `${line} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = testLine;
    }
  }
  lines.push(line);
  const startY = centerY - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((lineText, index) => ctx.fillText(lineText, centerX, startY + index * lineHeight));
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number) {
  const words = text.split(' ');
  let line = '';
  let currentY = y;
  for (const word of words) {
    const testLine = line ? `${line} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && line) {
      ctx.fillText(line, x, currentY);
      line = word;
      currentY += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x, currentY);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block space-y-2">
      <span className="text-sm font-bold uppercase tracking-[0.16em] text-white/62">{label}</span>
      {children}
      {hint && <span className="block text-xs leading-5 text-white/48">{hint}</span>}
    </label>
  );
}

function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className="w-full rounded-2xl border border-white/12 bg-white/[0.055] px-4 py-4 text-white outline-none transition focus:border-cyan-300 focus:bg-white/[0.085]" />;
}

function getGoogleScriptUrl() {
  return window.CPP_CONFIG?.GOOGLE_SCRIPT_URL?.trim() || '';
}

function getGoogleFormConfig() {
  const responseUrl = window.CPP_CONFIG?.GOOGLE_FORM_RESPONSE_URL?.trim() || '';
  const entryIds = window.CPP_CONFIG?.GOOGLE_FORM_ENTRY_IDS || {};
  const requiredKeys = [
    'displayName',
    'mainSocialLink',
    'email',
    'capcutUid',
    'ccid',
    'isUS',
    'country',
    'discordUsername',
    'discordLanguage',
    'otherHandles',
    'posterName',
  ] as const;
  const isReady = Boolean(responseUrl) && requiredKeys.every((key) => typeof entryIds[key] === 'string' && entryIds[key]!.trim());
  return { responseUrl, entryIds, isReady };
}

function toGoogleFormParams(payload: FormState, entryIds: NonNullable<Window['CPP_CONFIG']>['GOOGLE_FORM_ENTRY_IDS']) {
  const params = new URLSearchParams();
  params.set('fvv', '1');
  params.set('pageHistory', '0');
  params.set(entryIds!.displayName!, payload.displayName.trim());
  params.set(entryIds!.mainSocialLink!, payload.mainSocialLink.trim());
  params.set(entryIds!.email!, payload.email.trim());
  params.set(entryIds!.capcutUid!, payload.capcutUid.trim());
  params.set(entryIds!.ccid!, payload.ccid.trim());
  params.set(entryIds!.isUS!, payload.isUS === 'yes' ? 'Yes' : 'No');
  params.set(entryIds!.country!, payload.country.trim());
  params.set(entryIds!.discordUsername!, payload.discordUsername.trim());
  params.set(entryIds!.discordLanguage!, payload.discordLanguage);
  params.set(entryIds!.otherHandles!, payload.otherHandles.trim());
  params.set(entryIds!.posterName!, payload.posterName.trim());
  return params;
}

function toGoogleSheetParams(payload: FormState) {
  const params = new URLSearchParams();
  params.set('timestamp', new Date().toISOString());
  params.set('displayName', payload.displayName.trim());
  params.set('mainSocialLink', payload.mainSocialLink.trim());
  params.set('email', payload.email.trim());
  params.set('capcutUid', payload.capcutUid.trim());
  params.set('ccid', payload.ccid.trim());
  params.set('isUS', payload.isUS === 'yes' ? 'Yes' : 'No');
  params.set('country', payload.country.trim());
  params.set('discordUsername', payload.discordUsername.trim());
  params.set('discordLanguage', payload.discordLanguage);
  params.set('otherHandles', payload.otherHandles.trim());
  params.set('posterName', payload.posterName.trim());
  params.set('source', 'CapCut CPP Onboarding');
  return params;
}

function App() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(initialForm);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<SubmitSuccess | null>(null);

  const totalSteps = 7;
  const stepTitle = ['Welcome', 'Basic Info', 'Contact', 'Poster', 'Community', 'Submit', 'Complete'][step];
  const posterName = form.posterName || form.displayName;

  const canProceed = useMemo(() => !validateByStep(step, { ...form, posterName }), [step, form, posterName]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setError('');
  }

  function next() {
    const nextForm = { ...form, posterName };
    const validation = validateByStep(step, nextForm);
    if (validation) {
      setError(validation);
      return;
    }
    if (step === 1 && !form.posterName.trim()) setForm((current) => ({ ...current, posterName: current.displayName }));
    setError('');
    setStep((value) => Math.min(value + 1, totalSteps - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function back() {
    setError('');
    setStep((value) => Math.max(value - 1, 0));
  }

  async function submit() {
    const payload = { ...form, posterName };
    const validation = validateAll(payload);
    if (validation) {
      setError(validation);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const googleForm = getGoogleFormConfig();
      if (googleForm.isReady) {
        await fetch(googleForm.responseUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
          body: toGoogleFormParams(payload, googleForm.entryIds),
        });
        const formRecordId = `form_${Date.now().toString(36)}`;
        setSuccess({ ok: true, recordId: formRecordId, registrationNumber: `CPP-${Date.now().toString(36).toUpperCase()}-${formRecordId.slice(-4).toUpperCase()}` });
        setStep(6);
        return;
      }

      const googleScriptUrl = getGoogleScriptUrl();
      if (googleScriptUrl) {
        await fetch(googleScriptUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
          body: toGoogleSheetParams(payload),
        });
        const staticRecordId = `google_${Date.now().toString(36)}`;
        setSuccess({ ok: true, recordId: staticRecordId, registrationNumber: `CPP-${Date.now().toString(36).toUpperCase()}-${staticRecordId.slice(-4).toUpperCase()}` });
        setStep(6);
        return;
      }

      const response = await fetch('/api/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const rawText = await response.text();
      let result: SubmitSuccess | SubmitFailure;
      try {
        result = JSON.parse(rawText) as SubmitSuccess | SubmitFailure;
      } catch {
        if (response.status === 404 && window.location.hostname.includes('aime-site')) {
          const previewRecordId = `preview_${Date.now().toString(36)}`;
          setSuccess({ ok: true, recordId: previewRecordId, registrationNumber: `CPP-${Date.now().toString(36).toUpperCase()}-${previewRecordId.slice(-4).toUpperCase()}` });
          setStep(6);
          return;
        }
        const previewHint = response.status === 404 ? 'The submission API was not found. Please deploy this project to Cloudflare Pages with Functions enabled and FEISHU_* environment variables configured.' : rawText;
        throw new Error(previewHint || 'Server returned a non-JSON response.');
      }
      if (!response.ok || !result.ok) {
        const failure = result as SubmitFailure;
        throw new Error(failure.details ? `${failure.message} ${failure.details}` : failure.message);
      }
      setSuccess(result);
      setStep(6);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#03070a] text-[#f4fffe]">
      <div className="pointer-events-none absolute left-[-18rem] top-[-20rem] h-[46rem] w-[46rem] rounded-full bg-cyan-600/22 blur-3xl" />
      <div className="pointer-events-none absolute right-[-22rem] top-[20rem] h-[54rem] w-[54rem] rounded-full bg-blue-600/20 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:72px_72px] opacity-30" />

      <div className="relative mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
        <header className="mb-8 flex items-center justify-between rounded-full border border-white/12 bg-black/30 px-4 py-3 backdrop-blur-xl">
          <a href="#top" className="text-sm font-black uppercase tracking-[0.22em] text-white">CapCut CPP</a>
          <nav className="hidden gap-5 text-sm font-bold text-white/62 sm:flex">
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-cyan-300">Contact</a>
          </nav>
          <button className="capcut-gradient rounded-full px-5 py-3 text-sm font-black text-black" onClick={() => setStep(1)} type="button">Apply</button>
        </header>

        <section id="top" className="space-y-8">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(380px,0.48fr)] lg:items-start">
            <div className="space-y-5 pt-5">
              <p className="text-sm font-black uppercase tracking-[0.34em] text-cyan-300">Invite-only creator onboarding</p>
              <h1 className="max-w-5xl text-6xl font-black leading-[0.88] tracking-[-0.08em] sm:text-7xl lg:text-8xl">
                Join the <span className="capcut-text-gradient">CapCut Creative Partner Program</span>.
              </h1>
              <p className="max-w-3xl text-lg leading-8 text-white/70 sm:text-xl">Apply, generate your welcome poster, and let the CapCut team activate your CPP benefits.</p>
            </div>

            <aside className="glass sticky top-5 rounded-[2rem] p-5 sm:p-6">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Step {step + 1} / {totalSteps}</p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.05em]">{stepTitle}</h2>
              </div>
              <div className="h-14 w-14 rounded-2xl border border-white/12 bg-white/[0.06] p-1">
                <div className="capcut-gradient h-full w-full rounded-[1rem]" />
              </div>
            </div>
            <div className="mb-7 h-2 overflow-hidden rounded-full bg-white/10">
              <div className="capcut-gradient h-full rounded-full transition-all" style={{ width: `${((step + 1) / totalSteps) * 100}%` }} />
            </div>
            <WizardStep step={step} form={form} update={update} posterName={posterName} submit={submit} submitting={submitting} success={success} setStep={setStep} />
            {error && <div className="mt-5 rounded-2xl border border-red-300/30 bg-red-500/12 p-4 text-sm leading-6 text-red-100">{error}</div>}
            {step < 6 && (
              <div className="mt-6 flex gap-3">
                {step > 0 && <button className="rounded-2xl border border-white/14 px-5 py-3 font-bold text-white/78" onClick={back} type="button">Back</button>}
                {step < 5 && <button className="capcut-gradient flex-1 rounded-2xl px-5 py-3 font-black text-black disabled:opacity-50" onClick={next} disabled={!canProceed} type="button">Continue</button>}
                {step === 5 && <button className="capcut-gradient flex-1 rounded-2xl px-5 py-3 font-black text-black disabled:opacity-50" onClick={submit} disabled={submitting} type="button">{submitting ? 'Submitting…' : 'Submit Application'}</button>}
              </div>
            )}
            </aside>
          </div>
          <IntroCarousel />
        </section>

        <WelcomeContent jumpToApply={() => setStep(1)} />
      </div>
    </main>
  );
}

function WizardStep({ step, form, update, posterName, submit, submitting, success, setStep }: {
  step: number;
  form: FormState;
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  posterName: string;
  submit: () => void;
  submitting: boolean;
  success: SubmitSuccess | null;
  setStep: (step: number) => void;
}) {
  if (step === 0) {
    return (
      <div className="space-y-5 text-white/72">
        <p>Start your CPP onboarding flow. You will share creator details, generate a welcome poster, and submit to the CapCut team.</p>
      </div>
    );
  }
  if (step === 1) {
    return (
      <div className="space-y-4">
        <Field label="Display Name *" hint="This is your poster display name."><TextInput value={form.displayName} onChange={(e) => update('displayName', e.target.value)} placeholder="e.g. Nova Frames" /></Field>
        <Field label="Primary social media link *"><TextInput value={form.mainSocialLink} onChange={(e) => update('mainSocialLink', e.target.value)} placeholder="Instagram / TikTok / YouTube / X profile URL" /></Field>
        <Field label="Email *"><TextInput value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="creator@email.com" type="email" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="CapCut UID *" hint="Exactly 19 digits."><TextInput value={form.capcutUid} onChange={(e) => update('capcutUid', e.target.value.replace(/\D/g, '').slice(0, 19))} placeholder="1234567890123456789" inputMode="numeric" /></Field>
          <Field label="CCID *" hint="8–10 digits. This is not your username handle."><TextInput value={form.ccid} onChange={(e) => update('ccid', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="12345678" inputMode="numeric" /></Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="US region? *"><select className="w-full rounded-2xl border border-white/12 bg-white/[0.055] px-4 py-4 text-white outline-none" value={form.isUS} onChange={(e) => update('isUS', e.target.value as FormState['isUS'])}><option value="">Select</option><option value="yes">Yes</option><option value="no">No</option></select></Field>
          <Field label="Registration country *"><TextInput value={form.country} onChange={(e) => update('country', e.target.value)} placeholder="United States" /></Field>
        </div>
      </div>
    );
  }
  if (step === 2) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-cyan-300/24 bg-cyan-400/10 p-4 text-sm leading-6 text-cyan-50">Questions? Contact <a className="font-black text-cyan-200" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</div>
        <Field label="Other social handles"><TextInput value={form.otherHandles} onChange={(e) => update('otherHandles', e.target.value)} placeholder="TikTok / Instagram / YouTube / X" /></Field>
      </div>
    );
  }
  if (step === 3) {
    return (
      <div className="space-y-5">
        <Field label="Poster display name *"><TextInput value={form.posterName || form.displayName} onChange={(e) => update('posterName', e.target.value)} placeholder="Your creator name" /></Field>
        <PosterCanvas name={posterName} />
      </div>
    );
  }
  if (step === 4) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-cyan-300/24 bg-cyan-400/10 p-4 text-sm leading-6 text-cyan-50">Optional: leave your Discord username and join the CPP community for updates, support, and creator opportunities.</div>
        <Field label="Discord username (optional)"><TextInput value={form.discordUsername} onChange={(e) => update('discordUsername', e.target.value)} placeholder="yourname" /></Field>
        <Field label="Discord language region"><select className="w-full rounded-2xl border border-white/12 bg-white/[0.055] px-4 py-4 text-white outline-none" value={form.discordLanguage} onChange={(e) => update('discordLanguage', e.target.value as FormState['discordLanguage'])}><option>English</option><option>Español</option><option>Português</option></select></Field>
        <a className="capcut-gradient inline-flex w-full justify-center rounded-2xl px-5 py-4 font-black text-black" href={DISCORD_URL} target="_blank" rel="noreferrer">Join CPP Discord Community</a>
      </div>
    );
  }
  if (step === 5) {
    return (
      <div className="space-y-5">
        <div className="rounded-3xl border border-white/12 bg-white/[0.045] p-5">
          <p className="text-sm uppercase tracking-[0.22em] text-cyan-300">Review</p>
          <dl className="mt-4 grid gap-3 text-sm text-white/72">
            <div><dt className="font-black text-white">Creator</dt><dd>{form.displayName}</dd></div>
            <div><dt className="font-black text-white">Poster name</dt><dd>{posterName}</dd></div>
            <div><dt className="font-black text-white">Email</dt><dd>{form.email}</dd></div>
            <div><dt className="font-black text-white">CapCut UID</dt><dd>{form.capcutUid}</dd></div>
            <div><dt className="font-black text-white">CCID</dt><dd>{form.ccid}</dd></div>
            <div><dt className="font-black text-white">US region</dt><dd>{form.isUS === 'yes' ? 'Yes' : 'No'}</dd></div>
            {form.discordUsername.trim() && <div><dt className="font-black text-white">Discord username</dt><dd>{form.discordUsername}</dd></div>}
          </dl>
        </div>
        <button className="w-full rounded-2xl border border-white/14 px-5 py-3 font-bold text-white/78" onClick={() => setStep(3)} type="button">Edit poster</button>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-cyan-300/32 bg-cyan-400/12 p-5">
        <p className="text-sm uppercase tracking-[0.24em] text-cyan-200">Application received</p>
        <h3 className="mt-3 text-3xl font-black tracking-[-0.04em]">Congratulations, you are now part of CPP.</h3>
      </div>
      <p className="text-sm leading-6 text-white/72">Credits and corresponding benefits will be issued within one week. The CapCut team will contact you if any additional information is needed.</p>
    </div>
  );
}

function WelcomeContent({ jumpToApply }: { jumpToApply: () => void }) {
  return (
    <section className="mt-16 space-y-12 pb-20">
      <div className="grid gap-4 md:grid-cols-5">
        {benefits.map(([title, copy], index) => (
          <article key={title} className="glass rounded-3xl p-5">
            <p className="text-sm font-black text-cyan-300">0{index + 1}</p>
            <h3 className="mt-4 text-xl font-black tracking-[-0.04em]">{title}</h3>
            <p className="mt-3 text-sm leading-6 text-white/62">{copy}</p>
          </article>
        ))}
      </div>

      <div className="glass rounded-[2rem] p-5 sm:p-8">
        <div className="mb-7 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.28em] text-cyan-300">How you grow</p>
            <h2 className="mt-2 text-4xl font-black tracking-[-0.06em] sm:text-6xl">Five levels of creator upside.</h2>
          </div>
          <button className="capcut-gradient rounded-2xl px-6 py-4 font-black text-black" onClick={jumpToApply} type="button">Apply / Join Now</button>
        </div>
        <div className="grid gap-4 lg:grid-cols-5">
          {incentives.map(([title, copy]) => (
            <div key={title} className="rounded-3xl border border-white/12 bg-black/28 p-5">
              <h3 className="text-lg font-black text-white">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-white/62">{copy}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.28em] text-cyan-300">Creator proof</p>
            <h2 className="mt-2 text-4xl font-black tracking-[-0.06em] sm:text-6xl">Built with creators already pushing AI video forward.</h2>
          </div>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {creators.map((creator) => (
            <a key={creator.name} href={creator.link} target="_blank" rel="noreferrer" className="glass group overflow-hidden rounded-[2rem] p-4 transition hover:-translate-y-1">
              <img className="aspect-square w-full rounded-[1.5rem] object-cover" src={creator.avatar} alt={`${creator.name} avatar`} />
              <div className="p-3">
                <h3 className="text-2xl font-black tracking-[-0.05em]">{creator.name}</h3>
                <p className="mt-1 text-sm text-white/58">{creator.role} · View on X →</p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function mountApp() {
  const root = document.getElementById('root');
  if (!root) return;
  createRoot(root).render(<App />);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountApp, { once: true });
} else {
  mountApp();
}
